import React, { useState, useEffect } from 'react';
import { useTargetAudience, type TargetAudienceData } from '../hooks/useTargetAudience';
import GenericFilterTab from './GenericFilterTab';
import CSVUploadTab from './CSVUploadTab';
import type { TargetAudienceFilter } from '../types/Campaign';

interface TargetAudienceSelectorProps {
  campaignId: string;
  initialData?: TargetAudienceData;
  onChange: (data: TargetAudienceData) => void;
  disabled?: boolean;
}

type TabType = 'filter' | 'upload';

const TargetAudienceSelector: React.FC<TargetAudienceSelectorProps> = ({
  campaignId,
  initialData,
  onChange,
  disabled = false
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('filter');
  const [filterData, setFilterData] = useState<TargetAudienceFilter>({
    region: '',
    industry: '',
    position: ''
  });
  const [uploadData, setUploadData] = useState<{ fileUrl: string; fileName: string }>({
    fileUrl: '',
    fileName: ''
  });
  const [validationMessage, setValidationMessage] = useState<string | null>(null);

  const { updateCampaignTargetAudience, updating } = useTargetAudience();

  // Initialize from props
  useEffect(() => {
    if (initialData) {
      if (initialData.type === 'generic_filter' && initialData.filter) {
        setFilterData(initialData.filter);
        setActiveTab('filter');
      } else if (initialData.type === 'csv_upload' && initialData.fileUrl) {
        setUploadData({
          fileUrl: initialData.fileUrl,
          fileName: initialData.fileName || 'uploaded_file.csv'
        });
        setActiveTab('upload');
      }
    }
  }, [initialData]);

  // Check for mutual exclusivity and update parent
  useEffect(() => {
    const hasFilter = filterData.region && filterData.industry && filterData.position;
    const hasUpload = uploadData.fileUrl;

    if (hasFilter && hasUpload) {
      setValidationMessage('⚠️ Please choose only one method: either Generic Filter OR CSV Upload, not both.');
      return;
    }

    setValidationMessage(null);

    if (hasFilter) {
      const data: TargetAudienceData = {
        type: 'generic_filter',
        filter: filterData
      };
      onChange(data);
    } else if (hasUpload) {
      const data: TargetAudienceData = {
        type: 'csv_upload',
        fileUrl: uploadData.fileUrl,
        fileName: uploadData.fileName
      };
      onChange(data);
    } else {
      onChange({});
    }
  }, [filterData, uploadData, onChange]);

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
  };

  const handleFilterChange = (filter: TargetAudienceFilter) => {
    setFilterData(filter);
  };

  const handleFileUploaded = (fileUrl: string, fileName: string) => {
    setUploadData({ fileUrl, fileName });
  };

  const handleSave = async () => {
    const hasFilter = filterData.region && filterData.industry && filterData.position;
    const hasUpload = uploadData.fileUrl;

    if (hasFilter && hasUpload) {
      setValidationMessage('Please choose only one method before saving.');
      return;
    }

    try {
      if (hasFilter) {
        await updateCampaignTargetAudience(campaignId, {
          type: 'generic_filter',
          filter: filterData
        });
      } else if (hasUpload) {
        await updateCampaignTargetAudience(campaignId, {
          type: 'csv_upload',
          fileUrl: uploadData.fileUrl,
          fileName: uploadData.fileName
        });
      }
    } catch (error) {
      console.error('Failed to save target audience:', error);
    }
  };

  const isComplete = () => {
    const hasFilter = filterData.region && filterData.industry && filterData.position;
    const hasUpload = uploadData.fileUrl;
    return (hasFilter || hasUpload) && !(hasFilter && hasUpload);
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900">Step 1: Target Audience Selection</h3>
        <p className="text-sm text-gray-600 mt-1">
          Choose how you want to target your audience - either by selecting criteria or uploading a custom list.
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex" aria-label="Tabs">
          <button
            onClick={() => handleTabChange('filter')}
            disabled={disabled}
            className={`w-1/2 py-3 px-6 text-sm font-medium border-b-2 focus:outline-none ${
              activeTab === 'filter'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            Generic Filter
          </button>
          <button
            onClick={() => handleTabChange('upload')}
            disabled={disabled}
            className={`w-1/2 py-3 px-6 text-sm font-medium border-b-2 focus:outline-none ${
              activeTab === 'upload'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            CSV Upload
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      <div className="p-6">
        {activeTab === 'filter' && (
          <GenericFilterTab
            filter={filterData}
            onChange={handleFilterChange}
            disabled={disabled}
          />
        )}

        {activeTab === 'upload' && (
          <CSVUploadTab
            campaignId={campaignId}
            fileUrl={uploadData.fileUrl}
            fileName={uploadData.fileName}
            onFileUploaded={handleFileUploaded}
            disabled={disabled}
          />
        )}
      </div>

      {/* Validation Warning */}
      {validationMessage && (
        <div className="px-6 py-4 bg-yellow-50 border-t border-yellow-200">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-700">{validationMessage}</p>
            </div>
          </div>
        </div>
      )}

      {/* Save Button */}
      <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-between items-center">
        <div className="flex items-center">
          {isComplete() && (
            <div className="flex items-center text-green-600">
              <svg className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="text-sm font-medium">Target audience configured</span>
            </div>
          )}
        </div>
        
        <button
          onClick={handleSave}
          disabled={disabled || updating || !isComplete() || !!validationMessage}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {updating ? 'Saving...' : 'Save Target Audience'}
        </button>
      </div>
    </div>
  );
};

export default TargetAudienceSelector;
